import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Palette } from 'lucide-react';

const DigitalLavaLamp = () => {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const blobsRef = useRef([]);
  const animationRef = useRef(null);
  const [colorScheme, setColorScheme] = useState('sunset');
  const [speed, setSpeed] = useState('medium');

  const colorSchemes = {
    sunset: { primary: '#FF6B9D', secondary: '#FFA07A', tertiary: '#FFD700', bg: '#1a1a2e' },
    ocean: { primary: '#4682B4', secondary: '#20B2AA', tertiary: '#00CED1', bg: '#0a192f' },
    forest: { primary: '#32CD32', secondary: '#90EE90', tertiary: '#7FFF00', bg: '#1a2e1a' },
    lavender: { primary: '#DDA0DD', secondary: '#E6E6FA', tertiary: '#FF69B4', bg: '#2e1a2e' }
  };

  const speedSettings = {
    slow: { multiplier: 0.3, label: 'Slow' },
    medium: { multiplier: 1.0, label: 'Medium' },
    fast: { multiplier: 2.5, label: 'Fast' }
  };

  const initBlobs = useCallback(() => {
    blobsRef.current = [];
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const numBlobs = 30;
    const minRadius = 15;
    const maxRadius = 40;
    
    for (let i = 0; i < numBlobs; i++) {
      const baseRadius = Math.random() * (maxRadius - minRadius) + minRadius;
      blobsRef.current.push({
        id: i,
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 3,
        vy: (Math.random() - 0.5) * 3,
        ax: 0,
        ay: 0,
        radius: baseRadius,
        baseRadius: baseRadius,
        mass: baseRadius * baseRadius,
        phase: Math.random() * Math.PI * 2,
        pulseSpeed: Math.random() * 0.03 + 0.01,
        heat: Math.random(),
        colorIndex: Math.floor(Math.random() * 3),
        merging: false
      });
    }
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    initBlobs();

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    window.addEventListener('resize', resize);

    return () => {
      window.removeEventListener('resize', resize);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [initBlobs]);

  useEffect(() => {
    const animate = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      
      const ctx = canvas.getContext('2d');
      const colors = colorSchemes[colorScheme];
      const colorArray = [colors.primary, colors.secondary, colors.tertiary];
      const speedMultiplier = speedSettings[speed].multiplier;

      // Dark background with subtle gradient
      const bgGradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
      bgGradient.addColorStop(0, colors.bg);
      bgGradient.addColorStop(1, adjustColor(colors.bg, 20));
      ctx.fillStyle = bgGradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Mercury-like merging behavior
      const mergeBlobs = () => {
        const blobs = blobsRef.current;
        const toRemove = new Set();
        
        for (let i = 0; i < blobs.length; i++) {
          if (toRemove.has(i)) continue;
          
          for (let j = i + 1; j < blobs.length; j++) {
            if (toRemove.has(j)) continue;
            
            const a = blobs[i];
            const b = blobs[j];
            const dx = b.x - a.x;
            const dy = b.y - a.y;
            const dist = Math.sqrt(dx * dx + dy * dy);
            const touchDist = a.radius + b.radius;
            
            if (dist < touchDist * 0.7) {
              const totalMass = a.mass + b.mass;
              const newRadius = Math.sqrt(totalMass);
              
              a.x = (a.x * a.mass + b.x * b.mass) / totalMass;
              a.y = (a.y * a.mass + b.y * b.mass) / totalMass;
              a.vx = (a.vx * a.mass + b.vx * b.mass) / totalMass;
              a.vy = (a.vy * a.mass + b.vy * b.mass) / totalMass;
              a.radius = newRadius;
              a.baseRadius = newRadius;
              a.mass = totalMass;
              
              toRemove.add(j);
            }
          }
        }
        
        if (toRemove.size > 0) {
          blobsRef.current = blobs.filter((_, idx) => !toRemove.has(idx));
        }
        
        const maxRadius = 80;
        const minRadius = 15;
        
        blobsRef.current.forEach((blob) => {
          if (blob.radius > maxRadius && Math.random() < 0.02) {
            const newRadius = blob.radius / Math.sqrt(2);
            blob.radius = newRadius;
            blob.baseRadius = newRadius;
            blob.mass = newRadius * newRadius;
            
            blobsRef.current.push({
              id: Date.now() + Math.random(),
              x: blob.x + (Math.random() - 0.5) * newRadius,
              y: blob.y + (Math.random() - 0.5) * newRadius,
              vx: -blob.vx + (Math.random() - 0.5) * 2,
              vy: -blob.vy + (Math.random() - 0.5) * 2,
              ax: 0,
              ay: 0,
              radius: newRadius,
              baseRadius: newRadius,
              mass: newRadius * newRadius,
              phase: Math.random() * Math.PI * 2,
              pulseSpeed: Math.random() * 0.03 + 0.01,
              heat: blob.heat,
              colorIndex: blob.colorIndex,
              merging: false
            });
          }
        });
        
        while (blobsRef.current.length < 15) {
          const baseRadius = Math.random() * (40 - minRadius) + minRadius;
          blobsRef.current.push({
            id: Date.now() + Math.random(),
            x: Math.random() * canvas.width,
            y: Math.random() < 0.5 ? canvas.height + 50 : -50,
            vx: (Math.random() - 0.5) * 2,
            vy: (Math.random() - 0.5) * 2,
            ax: 0,
            ay: 0,
            radius: baseRadius,
            baseRadius: baseRadius,
            mass: baseRadius * baseRadius,
            phase: Math.random() * Math.PI * 2,
            pulseSpeed: Math.random() * 0.03 + 0.01,
            heat: Math.random(),
            colorIndex: Math.floor(Math.random() * 3),
            merging: false
          });
        }
      };

      mergeBlobs();

      // Update blob positions with speed multiplier
      blobsRef.current.forEach(blob => {
        blob.heat += (Math.random() - 0.5) * 0.02;
        blob.heat = Math.max(0, Math.min(1, blob.heat));
        
        const buoyancy = (blob.heat - 0.5) * 0.08 * speedMultiplier;
        blob.ay = -buoyancy;
        
        blob.vx += blob.ax * speedMultiplier;
        blob.vy += blob.ay * speedMultiplier;
        
        const maxSpeed = 4 * speedMultiplier;
        const currentSpeed = Math.sqrt(blob.vx * blob.vx + blob.vy * blob.vy);
        if (currentSpeed > maxSpeed) {
          blob.vx = (blob.vx / currentSpeed) * maxSpeed;
          blob.vy = (blob.vy / currentSpeed) * maxSpeed;
        }
        
        blob.vx *= 0.995;
        blob.vy *= 0.995;
        
        blob.x += blob.vx * speedMultiplier;
        blob.y += blob.vy * speedMultiplier;
        
        // Wall bounce
        if (blob.x < blob.radius) { blob.x = blob.radius; blob.vx *= -0.6; }
        if (blob.x > canvas.width - blob.radius) { blob.x = canvas.width - blob.radius; blob.vx *= -0.6; }
        if (blob.y < blob.radius) { blob.y = blob.radius; blob.vy *= -0.6; blob.heat = Math.min(1, blob.heat + 0.1); }
        if (blob.y > canvas.height - blob.radius) { blob.y = canvas.height - blob.radius; blob.vy *= -0.6; blob.heat = Math.max(0, blob.heat - 0.1); }
        
        // Pulsing with speed influence
        blob.phase += blob.pulseSpeed * speedMultiplier;
        blob.radius = blob.baseRadius + Math.sin(blob.phase) * blob.baseRadius * 0.15;
      });

      // Metaball rendering
      const resolution = 6;
      const threshold = 1.0;
      const colorRgbArray = colorArray.map(hexToRgb);

      for (let x = 0; x < canvas.width; x += resolution) {
        for (let y = 0; y < canvas.height; y += resolution) {
          let sum = 0;
          let colorSum = [0, 0, 0];
          let colorWeight = 0;

          blobsRef.current.forEach(blob => {
            const dx = x - blob.x;
            const dy = y - blob.y;
            const distSq = Math.max(dx * dx + dy * dy, 0.0001);
            const influence = (blob.radius * blob.radius) / distSq;
            sum += influence;

            if (influence > 0.1) {
              const color = colorRgbArray[blob.colorIndex];
              colorSum[0] += color.r * influence;
              colorSum[1] += color.g * influence;
              colorSum[2] += color.b * influence;
              colorWeight += influence;
            }
          });

          if (sum > threshold && colorWeight > 0) {
            const intensity = Math.min(1, (sum - threshold) * 2);
            const r = Math.floor(colorSum[0] / colorWeight);
            const g = Math.floor(colorSum[1] / colorWeight);
            const b = Math.floor(colorSum[2] / colorWeight);

            ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${intensity})`;
            ctx.fillRect(x, y, resolution, resolution);
          }
        }
      }

      // Add glossy highlight effect
      blobsRef.current.forEach(blob => {
        const gradient = ctx.createRadialGradient(
          blob.x - blob.radius * 0.3, 
          blob.y - blob.radius * 0.3, 
          0, 
          blob.x, 
          blob.y, 
          blob.radius * 1.2
        );
        gradient.addColorStop(0, 'rgba(255, 255, 255, 0.4)');
        gradient.addColorStop(0.3, 'rgba(255, 255, 255, 0.15)');
        gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
        
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(blob.x, blob.y, blob.radius * 0.6, 0, Math.PI * 2);
        ctx.fill();
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [colorScheme, speed]);

  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : { r: 0, g: 0, b: 0 };
  };

  const adjustColor = (hex, amount) => {
    const rgb = hexToRgb(hex);
    const r = Math.min(255, Math.max(0, rgb.r + amount));
    const g = Math.min(255, Math.max(0, rgb.g + amount));
    const b = Math.min(255, Math.max(0, rgb.b + amount));
    return `rgb(${r}, ${g}, ${b})`;
  };

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ backgroundColor: colorSchemes[colorScheme].bg }}>
      <canvas
        ref={canvasRef}
        className="absolute inset-0"
      />

      <div className="relative z-10 p-6">
        <header className="flex justify-between items-center mb-8">
          <button
            data-testid="back-to-dashboard"
            onClick={() => navigate('/dashboard')}
            className="glass-effect text-white px-6 py-3 rounded-full font-semibold hover:bg-white/20 transition-all duration-300 flex items-center gap-2"
          >
            <ArrowLeft className="w-5 h-5" />
            Back
          </button>
          
          <h1 className="text-4xl font-bold text-white">Digital Lava Lamp</h1>
          
          <div className="w-24"></div>
        </header>

        {/* Speed selector */}
        <div className="fixed top-24 left-1/2 transform -translate-x-1/2 glass-effect rounded-2xl p-3">
          <div className="flex items-center gap-2">
            <span className="text-white text-sm mr-2">Speed:</span>
            {Object.entries(speedSettings).map(([key, { label }]) => (
              <button
                key={key}
                data-testid={`speed-${key}`}
                onClick={() => setSpeed(key)}
                className={`px-5 py-2 rounded-full font-semibold transition-all duration-300 ${
                  speed === key 
                    ? 'bg-white text-[#2D3748]' 
                    : 'bg-white/20 text-white hover:bg-white/30'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Color scheme selector */}
        <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 glass-effect rounded-2xl p-4">
          <div className="flex items-center gap-4">
            <Palette className="w-6 h-6 text-white" />
            {Object.keys(colorSchemes).map(scheme => (
              <button
                key={scheme}
                data-testid={`color-scheme-${scheme}`}
                onClick={() => setColorScheme(scheme)}
                className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 ${
                  colorScheme === scheme 
                    ? 'bg-white text-[#2D3748] scale-110' 
                    : 'bg-white/20 text-white hover:bg-white/30'
                }`}
              >
                {scheme.charAt(0).toUpperCase() + scheme.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DigitalLavaLamp;
